import { Col, Row, Spinner2, Text } from 'components';
import { Api } from 'lib';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Header, TableComponents } from 'screens/Admin/components';
import { AuditLogPanel } from './components';

const formatActionType = (str) =>
  str?.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || '-';

// initial action types placeholder (will be replaced by server data)
const ACTION_TYPES = [{ value: '', label: 'All Events' }];

const AdminAuditLogs = () => {
  const { t } = useTranslation();
  const tl = (key) => t(`admin.auditLogs.${key}`);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actorFilter, setActorFilter] = useState('');
  const [targetFilter, setTargetFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);

  const [filterOptions, setFilterOptions] = useState({ actors: [], target_users: [], action_types: [], entity_types: [], refund_request_statuses: [] });
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => {
    fetchLogs();
  }, [actorFilter, targetFilter, actionFilter, dateFrom, dateTo, offset, limit]);

  // load filter options once
  useEffect(() => {
    loadFilterOptions();
  }, []);

  async function fetchLogs() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (actorFilter) params.set('actor_id', actorFilter);
      if (targetFilter) params.set('target_user_id', targetFilter);
      if (actionFilter) params.set('action_type', actionFilter);
      if (dateFrom) params.set('date_from', new Date(`${dateFrom}T00:00:00.000Z`).toISOString());
      if (dateTo) params.set('date_to', new Date(`${dateTo}T23:59:59.999Z`).toISOString());
      if (offset) params.set('offset', String(offset));
      if (limit) params.set('limit', String(limit));

      const query = params.toString();
      const res = await Api.get(`/admin/auditLogs/list${query ? `?${query}` : ''}`);

      setLogs(Array.isArray(res?.data) ? res.data : []);
      setTotal(res?.pagination?.total || 0);
    } catch (err) {
      console.error('Error fetching audit logs', err);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }

  async function loadFilterOptions() {
    try {
      const res = await Api.get('/admin/auditLogs/filters');
      const data = res?.data || res;
      setFilterOptions(data);
      // update action types constant but keep "All" at front
      if (Array.isArray(data.action_types)) {
        ACTION_TYPES.length = 0;
        ACTION_TYPES.push({ value: '', label: tl('allActions') });
        data.action_types.forEach((at) => ACTION_TYPES.push({ value: at, label: formatActionType(at) }));
      }
    } catch (err) {
      console.error('Error fetching audit filters', err);
    }
  }

  function prevPage() {
    setOffset(Math.max(0, offset - limit));
  }

  function nextPage() {
    if (offset + limit < total) setOffset(offset + limit);
  }

  return (
    <Col noFlex wid="100%" ht="100vh" start>
      <Header>
        <Row center between wid="100%">
          <Text h3>{tl('title')}</Text>
        </Row>
      </Header>

      <div className="flex flex-col mt-[54px] h-[calc(100vh-54px)]">
        {/* Filter bar */}
        <div className="flex-shrink-0 border-b border-[#E2E8F0] bg-white px-6 py-4">
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 items-end">
            <label className="flex flex-col gap-1">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-[#64748B]">
                Actor
              </span>
              <select
                value={actorFilter}
                onChange={(e) => setActorFilter(e.target.value)}
                className="h-10 rounded-lg border border-[#CBD5E1] px-3 text-[13px] text-[#334155] bg-white cursor-pointer"
              >
                <option value="">{tl('allActors')}</option>
                {filterOptions.actors.map((a) => (
                  <option key={a.id} value={a.id}>{`${a.name} (${a.email})`}</option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-[#64748B]">
                Target
              </span>
              <select
                value={targetFilter}
                onChange={(e) => setTargetFilter(e.target.value)}
                className="h-10 rounded-lg border border-[#CBD5E1] px-3 text-[13px] text-[#334155] bg-white cursor-pointer"
              >
                <option value="">{tl('allTargets')}</option>
                {filterOptions.target_users.map((u) => (
                  <option key={u.id} value={u.id}>{`${u.name} (${u.email})`}</option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-[#64748B]">
                Action
              </span>
              <select
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                className="h-10 rounded-lg border border-[#CBD5E1] px-3 text-[13px] text-[#334155] bg-white cursor-pointer"
              >
                {ACTION_TYPES.map((a) => (
                  <option key={a.value} value={a.value}>{a.label}</option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-[#64748B]">
                From
              </span>
              <div className="relative">
                <svg className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="h-10 w-full rounded-lg border border-[#CBD5E1] pl-9 pr-3 text-[13px] text-[#334155] bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6]"
                />
              </div>
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-[#64748B]">
                To
              </span>
              <div className="relative">
                <svg className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="h-10 w-full rounded-lg border border-[#CBD5E1] pl-9 pr-3 text-[13px] text-[#334155] bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6]"
                />
              </div>
            </label>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto bg-[#FAFBFC]">
          {loading && (
            <Col wid="100%" marg="40px 0" centerAll noFlex>
              <Spinner2 />
            </Col>
          )}

          {!loading && logs.length === 0 && (
            <Col wid="100%" centerAll noFlex marg="40px 0">
              <Text>{tl('noLogs')}</Text>
            </Col>
          )}

          {!loading && logs.length > 0 && (
            <TableComponents.List
              loading={loading}
              Header={() => (
                <>
                  <TableComponents.Th first>
                    <Text>{tl('table.actor')}</Text>
                  </TableComponents.Th>
                  <TableComponents.Th>
                    <Text>{tl('table.target')}</Text>
                  </TableComponents.Th>
                  <TableComponents.Th>
                    <Text>{tl('table.action')}</Text>
                  </TableComponents.Th>
                  <TableComponents.Th>
                    <Text>{tl('table.time')}</Text>
                  </TableComponents.Th>
                  <TableComponents.Th last>
                    <Text>{tl('table.ip')}</Text>
                  </TableComponents.Th>
                </>
              )}
              Items={() =>
                logs.map((l) => (
                  <TableComponents.Tr
                    key={l._id}
                    onClick={() => setSelectedLog(l)}
                  >
                    <TableComponents.Td first>
                      <Text>
                        {l.actor_id?.name || l.actor_email || l.actor_id?.email || '-'}
                      </Text>
                    </TableComponents.Td>
                    <TableComponents.Td>
                      <Text>
                        {l.target_user_id?.name || l.target_user_id?.email || '-'}
                      </Text>
                    </TableComponents.Td>
                    <TableComponents.Td>
                      <Text>{formatActionType(l.action_type)}</Text>
                    </TableComponents.Td>
                    <TableComponents.Td>
                      <Text>{new Date(l.timestamp ?? l.createdAt).toLocaleString()}</Text>
                    </TableComponents.Td>
                    <TableComponents.Td last>
                      <Text>{l.ip_address || '-'}</Text>
                    </TableComponents.Td>
                  </TableComponents.Tr>
                ))
              }
            />
          )}

          <AuditLogPanel log={selectedLog} visible={!!selectedLog} closePanel={() => setSelectedLog(null)} />
        </div>

        {/* Pagination footer */}
        <div className="flex-shrink-0 border-t border-[#E2E8F0] bg-white px-6 py-3 flex items-center justify-between">
          <div className="text-[13px] text-[#64748B]">
            {total > 0 ? `${Math.min(total, offset + 1)}–${Math.min(total, offset + limit)} of ${total}` : ''}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={prevPage}
              disabled={offset === 0}
              className="h-9 px-4 rounded-lg border border-[#CBD5E1] text-[13px] text-[#334155] bg-white hover:bg-[#F1F5F9] disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              {tl('prev')}
            </button>
            <button
              onClick={nextPage}
              disabled={offset + limit >= total}
              className="h-9 px-4 rounded-lg border border-[#CBD5E1] text-[13px] text-[#334155] bg-white hover:bg-[#F1F5F9] disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              {tl('next')}
            </button>
          </div>
        </div>
      </div>
    </Col>
  );
};

export default AdminAuditLogs;